import { usePassportProvider } from "@/context/PassportProvider";
import { useAccessToken } from "@/hooks";
import { authorizedFetcherGet } from "@/utils/request";
import { trackEvent } from "@analytics";
import { Box, Button, Stack } from "@biom3/react";
import { Alert, AlertTitle } from "@mui/material";
import Link from "next/link";
import useSWR from "swr";
import { ImxFoundation } from "./ImxFoundation";
import { RewardsBanner } from "./RewardsBanner";
import { TranslucentContainer } from "./TranslucentContainer";
import type { UserProfile } from "./types";

export const KYCVerification = () => {
  const { walletAddress } = usePassportProvider();
  const getAccessToken = useAccessToken();
  const { data: userProfile, isLoading } = useSWR<UserProfile>(
    walletAddress ? ["/v2/rewards/profile", walletAddress] : null,
    ([path, _]) => authorizedFetcherGet(path, getAccessToken),
  );

  const isRejected = userProfile?.kyc_status === "rejected";

  const handleStartKYCClick = () => {
    trackEvent({
      component: "Button",
      action: "Pressed",
      extras: {
        control: "StartKYC",
        section: "WeeklyRewards",
        userJourney: "Rewards",
      },
    });
  };

  return (
    <TranslucentContainer>
      <Stack direction="column" sx={{ minHeight: "400px" }}>
        <Box sx={{ flex: 1, py: "base.spacing.x6" }}>
          <RewardsBanner
            title="Verify your identity to keep unlocking rewards"
            isLoading={isLoading}
            description={
              !isRejected ? (
                <>
                  Submitting the form takes a few minutes, and our team will
                  review it within thirty business days. <br />
                  Once approved, you can claim this reward and join future
                  weekly draws.
                </>
              ) : null
            }
          />

          {isRejected && (
            <Alert severity="error">
              <AlertTitle>We could not verify your documents</AlertTitle>
              Please email{" "}
              <a
                href="mailto:kyc@immutable.com"
                style={{ textDecoration: "underline" }}
              >
                kyc@immutable.com
              </a>{" "}
              with your name, email and Passport wallet address so our team can
              help you resolve the issue. Rewards remain on hold until
              verification is complete.
            </Alert>
          )}

          <Stack
            gap="base.spacing.x4"
            direction="row"
            sx={{ py: "base.spacing.x6" }}
          >
            {!isRejected && (
              <Button
                rc={
                  <a
                    href="https://docsend.com/view/4k24fx2zu25knwxa"
                    target="_blank"
                    rel="noreferrer"
                    onClick={handleStartKYCClick}
                  />
                }
              >
                Start KYC
              </Button>
            )}
            <Button variant="secondary" rc={<Link href="/rewards/faq/" />}>
              Learn more
            </Button>
          </Stack>
        </Box>
        <ImxFoundation />
      </Stack>
    </TranslucentContainer>
  );
};
